import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { handleApiError } from "../utility/security/errorHandling";
import { clearSessionData } from "../utility/security/sessionUtils";
import { AppBootSkeleton } from "../components/UI/DesignStandards";
import * as Sentry from "@sentry/react";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => {
    if (typeof document === "undefined") return false;
    const root = document.getElementById("root");
    return root?.getAttribute("data-prerendered") !== "true";
  });

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setUser(user);
      setLoading(false);

      // Set user context in Sentry
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.user_metadata?.username || user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    // Listen for changes on auth state
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setUser(user);
      setLoading(false);

      // Set user context in Sentry
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.user_metadata?.username || user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password, captchaToken = null) => {
    try {
      const signInOptions = {
        email,
        password,
      };

      // Add CAPTCHA token if provided
      if (captchaToken) {
        signInOptions.options = {
          captchaToken: captchaToken,
        };
      }

      const { error } = await supabase.auth.signInWithPassword(signInOptions);
      if (error) throw error;
    } catch (err) {
      const handledError = handleApiError(err);
      throw new Error(handledError.message);
    }
  };

  const signUp = async (email, password) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
    } catch (err) {
      const handledError = handleApiError(err);
      throw new Error(handledError.message);
    }
  };

  const signOut = async () => {
    try {
      // Try to sign out from Supabase (may fail if session is invalid, but that's okay)
      const { error } = await supabase.auth.signOut();
      // Don't throw on error - we'll clear local data anyway
      if (error) {
        console.warn(
          "Sign out error (continuing with local cleanup):",
          error.message
        );
      }
    } catch (err) {
      // Ignore errors - we'll clear local data anyway
      console.warn(
        "Sign out error (continuing with local cleanup):",
        err.message
      );
    } finally {
      // Always clear local session data regardless of server response
      clearSessionData();

      // Clear Supabase session from localStorage (where it's persisted)
      try {
        localStorage.removeItem(
          `sb-${
            process.env.REACT_APP_SUPABASE_URL?.split("//")[1]?.split(".")[0]
          }-auth-token`
        );
        // Also try clearing all Supabase-related localStorage items
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("sb-") || key.includes("supabase")) {
            localStorage.removeItem(key);
          }
        });
      } catch (storageErr) {
        console.warn("Error clearing localStorage:", storageErr);
      }

      // Clear user state
      setUser(null);

      // Force a hard refresh to ensure everything is cleared
      // This ensures any cached state is cleared
      window.location.href = "/signin";
    }

    return true;
  };

  const value = {
    signUp,
    signIn,
    signOut,
    user,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AppBootSkeleton /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
